import { Check, Moon, Palette, RotateCcw, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ACCENT_PRESETS, applyAccents, normalizeHex } from '../accent';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import { useToast } from './ui';

/** Account page: the accent colour of the app in light mode and in dark mode, previewed live and saved on the profile. */
export function AccentPicker() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const saved = { light: user?.accentLight ?? '', dark: user?.accentDark ?? '' };
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDraft({ light: user?.accentLight ?? '', dark: user?.accentDark ?? '' }), [user?.accentLight, user?.accentDark]);
  // Preview while choosing; leaving the page without saving goes back to the saved colours.
  useEffect(() => {
    applyAccents(draft.light, draft.dark);
    return () => applyAccents(user?.accentLight, user?.accentDark);
  }, [draft.light, draft.dark, user?.accentLight, user?.accentDark]);
  if (!user) return null;
  const dirty = draft.light !== saved.light || draft.dark !== saved.dark;

  async function save() {
    setBusy(true);
    try {
      await api.updateMe({ accentLight: draft.light, accentDark: draft.dark });
      await refresh();
      toast(t('account.accent_saved'));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('account.update_failed'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const row = (mode: 'light' | 'dark') => {
    const value = draft[mode];
    const set = (v: string) => setDraft((d) => ({ ...d, [mode]: v }));
    return (
      <div className="accent-row">
        <div className="label-text">
          {mode === 'light' ? <Sun className="inline-icon" /> : <Moon className="inline-icon" />} {t(mode === 'light' ? 'account.accent_light' : 'account.accent_dark')}
        </div>
        <div className="accent-swatches" role="radiogroup" aria-label={t(mode === 'light' ? 'account.accent_light' : 'account.accent_dark')}>
          <button type="button" role="radio" aria-checked={!value} className={`accent-swatch default${!value ? ' on' : ''}`} title={t('account.accent_default')} aria-label={t('account.accent_default')} onClick={() => set('')}>
            {!value ? <Check /> : <RotateCcw />}
          </button>
          {ACCENT_PRESETS.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={value === c} className={`accent-swatch${value === c ? ' on' : ''}`} style={{ background: c }} title={c} aria-label={c} onClick={() => set(c)}>
              {value === c && <Check />}
            </button>
          ))}
          <label className={`accent-swatch custom${value && !ACCENT_PRESETS.includes(value) ? ' on' : ''}`} title={t('account.accent_custom')} style={value && !ACCENT_PRESETS.includes(value) ? { background: value } : undefined}>
            <Palette />
            <input type="color" aria-label={t(mode === 'light' ? 'account.accent_custom_light' : 'account.accent_custom_dark')} value={normalizeHex(value) ?? '#3fae4c'} onChange={(e) => set(e.target.value)} />
          </label>
          <span className="accent-code mono small muted">{value || t('account.accent_default')}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="stack gap-12 accent-picker">
      {row('light')}
      {row('dark')}
      <p className="hint">{t('account.accent_hint')}</p>
      <div className="row gap-8">
        <button type="button" className="btn primary sm" disabled={!dirty || busy} onClick={() => void save()}>
          {t('account.accent_save')}
        </button>
        <button type="button" className="btn sm" disabled={!dirty || busy} onClick={() => setDraft(saved)}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
